/* ==========================================================================
   SHX §3.18 — Before & After slider attention hint.

   Sweeps the divider once, the first time a slider scrolls into view, so
   visitors realise the image is draggable (the Stretch/Rosental pattern).

   It drives ONLY the vendor's own range input and fires that input's `input`
   event, which is exactly how a real drag reaches Shrine's <comparison-slider>
   element. No vendor class, style or internal is touched, so this stays safe
   across Shrine updates: if the custom element ever stops listening, the hint
   simply does nothing.

   Behaviour: runs once per slider per page view, waits until the slider is
   properly on screen, aborts the instant the visitor interacts, and never runs
   at all under prefers-reduced-motion.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var LEG_MS = 620;
  var START_DELAY = 320;

  /* easeInOutCubic — slow at the ends, quick through the middle, so the sweep
     reads as a deliberate gesture rather than a mechanical loop. */
  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function sweep(slider) {
    var input = slider.querySelector('.comparison-slider__input');
    if (!input) return;

    var travel = parseFloat(slider.getAttribute('data-shx-hint')) || 18;
    var start = parseFloat(input.value);
    if (isNaN(start)) start = 50;

    var min = parseFloat(input.min);
    var max = parseFloat(input.max);
    if (isNaN(min)) min = 0;
    if (isNaN(max)) max = 100;

    /* Clamp so the sweep never slams into either end of the range. */
    var right = Math.min(start + travel, max);
    var left = Math.max(start - travel, min);
    var stops = [start, right, left, start];

    var leg = 0;
    var legStart = null;
    var cancelled = false;

    function cancel() {
      cancelled = true;
    }

    /* Any hint of interaction hands control straight back to the visitor. */
    ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'wheel'].forEach(function (evt) {
      slider.addEventListener(evt, cancel, { once: true, passive: true });
    });

    function frame(now) {
      if (cancelled) return;
      if (legStart === null) legStart = now;

      var progress = Math.min((now - legStart) / LEG_MS, 1);
      var from = stops[leg];
      var to = stops[leg + 1];

      input.value = String(from + (to - from) * ease(progress));
      input.dispatchEvent(new Event('input', { bubbles: true }));

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else if (leg < stops.length - 2) {
        leg += 1;
        legStart = null;
        requestAnimationFrame(frame);
      } else {
        /* Land exactly on the original value so nothing drifts. */
        input.value = String(start);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    requestAnimationFrame(frame);
  }

  function watch(slider) {
    if (slider.dataset.shxHinted) return;

    if (REDUCED && REDUCED.matches) {
      slider.dataset.shxHinted = 'reduced-motion';
      return;
    }

    if (!('IntersectionObserver' in window)) {
      slider.dataset.shxHinted = 'no-io';
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || slider.dataset.shxHinted) return;
          slider.dataset.shxHinted = '1';
          observer.disconnect();
          window.setTimeout(function () {
            sweep(slider);
          }, START_DELAY);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(slider);
  }

  function init() {
    document.querySelectorAll('comparison-slider[data-shx-hint]').forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-arm when the merchant edits or re-adds the section. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
